import { useNavigate } from "react-router-dom";
import paths from "../../../../../api/paths.api";
import { buildEstimationHref } from "../utils/estimationLinks";
import { wbsLabel } from "../utils/treeLabels";
import { plantNodeId } from "../utils/treeNodeIds";
import { procurementItemHref } from "../utils/procurementHelpers";

/**
 * Node-click routing and the confirm-then-navigate flow for leaves that open
 * full editable forms on other pages (PV Input Data / Estimation).
 * Drawings navigate straight to BomPage with no confirmation.
 *
 * Rebuilt targets resolve their full context (plant/wbs/doc) without
 * re-parsing ids; ids the registry doesn't know are ignored by selection.
 */
export const useTreeSelection = ({
  treeData,
  onSelect,
  pickerMode = false,
  focusSelectedPlant = false,
  focusedPlantId,
  setFocusedPlantId,
  nodeTargetRef,
  confirmTarget,
  setConfirmTarget,
}) => {
  const navigate = useNavigate();
  const {
    plants,
    search,
    handleSearchChange,
    expanded,
    handleNodeToggle,
    loadMcsTreeForPlant,
    setExpanded,
  } = treeData;

  // A search looks across every project, so starting one drops the focus -
  // otherwise matches outside the picked plant would silently never show up.
  // Picking a result sets the focus and clears the search in the same click,
  // and the query trails that by a debounce.
  const focusedPlant =
    focusSelectedPlant && focusedPlantId
      ? plants.find((plant) => plant.id === focusedPlantId)
      : null;

  const visiblePlants = plants.filter(treeData.isPlantVisible).filter(
    (plant) => !focusedPlant || plant.id === focusedPlant.id,
  );

  // Typing steps back out of any drill-down - results span every project, and
  // a focus left in place would hide most of them.
  const handleSearchInput = (value) => {
    setFocusedPlantId(null);
    // Emptying the box puts the full list back, so it comes back collapsed:
    // the plants and discipline folders a search opened to show its matches
    // have no reason to stay open once there's nothing to match.
    if (!value.trim()) setExpanded([]);
    handleSearchChange(value);
  };

  const openDrawing = (target) => {
    // BomPage (PMSTreeComponents/BomPage) is mounted on
    // /plant/:plantId/drawing/:drawingId and loads the drawing itself via
    // getDrawingData(drawingId).
    const base = (paths.plantDrawing || "/plant/:plantId/drawing/:drawingId")
      .replace(":plantId", target.plantId)
      .replace(":drawingId", target.drawingId);
    navigate(base);
  };

  const handleNodeSelect = (event, nodeId) => {
    const target = nodeTargetRef.current[nodeId];
    if (!target) return;

    if (focusSelectedPlant && target.kind === "plant") {
      if (search) handleSearchChange("");
      setFocusedPlantId(target.plant.id);
      const id = plantNodeId(target.plant.id);
      if (!expanded.includes(id)) {
        handleNodeToggle(event, [...expanded, id]);
      } else {
        loadMcsTreeForPlant(target.plant.id);
      }
    }

    if (target.kind === "procurement") {
      const href = procurementItemHref(target);
      if (href) navigate(href);
      return;
    }

    if (target.kind === "module") {
      const hash = target.item.hash
        ? `#${new URLSearchParams(target.item.hash).toString()}`
        : "";
      const base = target.item.path
        ? `/plant/${target.plantId}/${target.item.path}`
        : `/plant/${target.plantId}`;
      // A specific sub-tab was picked (not just the module's flat tile), so
      // land on just that tab - see useStandaloneView.
      const query = target.item.hash ? "?standalone=1" : "";
      navigate(`${base}${query}${hash}`);
      return;
    }

    if (!pickerMode && target.kind === "drawing") {
      openDrawing(target);
      return;
    }

    if (!pickerMode && target.kind === "pv") {
      setConfirmTarget(target);
      return;
    }

    onSelect?.(target);
  };

  const handleConfirmOpen = () => {
    if (!confirmTarget) return;

    if (confirmTarget.kind === "drawing") {
      openDrawing(confirmTarget);
      setConfirmTarget(null);
      return;
    }

    // Structure numbers its own new revision once its data is loaded, so the
    // link carries no revision for that case - everything else names one.
    // `wbsType` is which half of Piping to open: projectId above can be a
    // different WBS (a Spec WBS links to the row its data lives on), so the
    // module cannot infer this from the project it loads - the tree has to
    // name it.
    navigate(
      buildEstimationHref({
        plantId: confirmTarget.plantId,
        projectId: confirmTarget.projectId,
        projectName: confirmTarget.projectName,
        revision: confirmTarget.revision,
        topTabLabel: confirmTarget.topTabLabel,
        subTabLabel: confirmTarget.subTabLabel,
        estimationSubTabLabel: confirmTarget.estimationSubTabLabel,
        wbsType: confirmTarget.wbsType,
        newRevision: confirmTarget.kind === "new-revision",
      }),
    );
    setConfirmTarget(null);
  };

  const handleConfirmCancel = () => setConfirmTarget(null);

  // The WBS row's "Add Document" - the register is kept per project, not
  // per WBS, so this opens the project's Document Register page rather than
  // a dialog of its own. Straight there, no confirmation - the tree does
  // not ask before opening a page that shows a list.
  //
  // The WBS clicked from, and its newest revision, ride along in the query:
  // the register's "Create New Doc Register" dialog fills its WBS and Latest
  // Revision fields from them, rather than asking again for what this click
  // already said.
  const openDocumentRegister = (plant, wbs, latestRevision) => {
    const base = (
      paths.plantDocRegister || "/plant/:plantId/document-register"
    ).replace(":plantId", plant.id);

    const query = new URLSearchParams();
    if (wbs?.id) {
      query.set("projectId", String(wbs.id));
      // Its name too: the register's own WBS list is fetched filtered by
      // clientName, and a WBS written by another tool can carry a different
      // one - with the name in hand the dialog can show this WBS either way.
      query.set("projectName", wbsLabel(wbs));
    }
    if (latestRevision != null && String(latestRevision) !== "") {
      query.set("revision", String(latestRevision));
    }

    const search = query.toString();
    navigate(search ? `${base}?${search}` : base);
  };

  return {
    focusedPlant,
    visiblePlants,
    handleSearchInput,
    handleNodeSelect,
    handleConfirmOpen,
    handleConfirmCancel,
    openDocumentRegister,
  };
};
